import type { SchematicPoint } from '@ghp/domain'

/**
 * La vue : quelle portion du dessin l'écran montre. C'est le `viewBox` du SVG, donc le zoom et
 * le glissement du plan tiennent en quatre nombres — rien à repeindre, le navigateur compose.
 *
 * ⚠️ Ce fichier est **pur** : il ne connaît ni React, ni SVG, ni le DOM. Il reçoit la boîte
 * mesurée, il ne la mesure pas. C'est ce qui rend éprouvable la conversion « doigt → dessin »,
 * exactement pour la raison que `signature-bitmap.ts` énonce sur la zone de signature : c'est
 * la seule partie qui se paie cher quand on la rate, et jsdom ne mesure rien.
 */
export interface SchematicViewport {
  x: number
  y: number
  width: number
  height: number
}

/** La boîte que la zone de dessin occupe à l'écran, telle qu'un navigateur la mesure. */
export interface ViewportBox {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Le plan de dessin au repos, en **unités de document**.
 *
 * ⚠️ Ces unités ne sont ni des pixels d'écran ni des mètres, et c'est ce qui rend un dessin
 * reproductible : deux agents sur deux tablettes de tailles différentes produisent le même
 * JSON, et le rendu d'archive ne dépend pas du matériel de celui qui a dessiné.
 */
export const SCHEMATIC_VIEW: SchematicViewport = { x: 0, y: 0, width: 1600, height: 1000 }

/**
 * Les bornes du zoom.
 *
 * Sans plancher, un dézoom insistant fait disparaître le schéma dans un point dont l'agent ne
 * sait plus revenir ; sans plafond, les coordonnées posées deviennent des fractions que le
 * dessin porte jusqu'au relevé transmis.
 */
export const MIN_SCHEMATIC_ZOOM = 0.25
export const MAX_SCHEMATIC_ZOOM = 4

/** Les quatre bordures d'un élément, en pixels CSS. */
export interface BoxInsets {
  left: number
  top: number
  right: number
  bottom: number
}

/**
 * La zone de dessin **sans sa bordure**.
 *
 * ⚠️ `getBoundingClientRect()` rend la boîte de **bordure**, et le plan porte un liseré. S'en
 * servir telle quelle posait chaque trait sous et à droite du stylet — c'est le défaut que
 * `SignaturePad` documente avoir corrigé sur la zone de signature, et il se réintroduit
 * silencieusement, puisque l'écart vaut la largeur d'un cheveu au centre et n'apparaît qu'aux
 * bords.
 */
export function contentBoxOf(bounds: ViewportBox, border: BoxInsets): ViewportBox {
  return {
    left: bounds.left + border.left,
    top: bounds.top + border.top,
    width: bounds.width - border.left - border.right,
    height: bounds.height - border.top - border.bottom,
  }
}

/**
 * Le point du dessin sous le doigt.
 *
 * ⚠️ **Une seule échelle, plus un centrage** — et c'est ce que le navigateur fait. Faute de
 * `preserveAspectRatio` explicite, un `<svg>` applique `xMidYMid meet` : il met le `viewBox` à
 * l'échelle **uniformément** et centre le résultat dans la zone, laissant deux bandes vides sur
 * le côté le plus long. Deux échelles indépendantes, une par axe, ne coïncident avec ce cadrage
 * que sur une zone au rapport exact du plan — que la mise en page ne garantit jamais. Mesuré sur
 * une tablette 800 × 1280 en portrait : jusqu'à 148 px entre le doigt et le trait, au coin.
 *
 * ⚠️ Corriger par `preserveAspectRatio="none"` serait le mauvais bout : le dessin se
 * déformerait, et l'image d'archive ne montrerait plus ce que l'agent a vu.
 *
 * ⚠️ **L'échelle est relue à chaque événement**, comme dans la zone de signature : une rotation
 * de la tablette, une barre de défilement qui disparaît ou un zoom en cours de tracé changent le
 * rapport, et une échelle mémorisée poserait la forme à côté du doigt.
 *
 * ⚠️ Une boîte non mesurée — premier rendu, onglet pas encore affiché, jsdom — vaut `0`, et une
 * division par zéro produirait un `Infinity` que le dessin emporterait jusqu'au relevé transmis.
 * On retombe alors sur une échelle de 1 : la forme se pose quelque part plutôt que nulle part.
 */
export function pointAt(
  view: SchematicViewport,
  box: ViewportBox,
  client: { clientX: number; clientY: number },
): SchematicPoint {
  const scale = scaleOf(view, box)
  const offset = centeringOf(view, box, scale)

  return {
    x: rounded(view.x + (client.clientX - box.left - offset.x) / scale),
    y: rounded(view.y + (client.clientY - box.top - offset.y) / scale),
  }
}

/**
 * Le dixième d'unité de document — un vingtième de pixel à l'écran, donc invisible.
 *
 * ⚠️ Ce n'est pas de la cosmétique, c'est du **poids transmis**. Une division flottante non
 * arrondie écrit `{"x":0.9259259259259259,…}`, soit 44 octets par point là où un dixième en
 * demande 22 ; un tracé au crayon d'une minute en pose plusieurs milliers, et le dépôt d'une
 * proposition est plafonné à 1 Mo. Le `413` qui suit tombe avant le contrôleur, sans message
 * français, et la file le classe en échec **définitif** : le contrôle n'est plus transmissible,
 * et rien ne dit à l'agent que la cause est son crayon.
 */
function rounded(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Zoom **autour du centre de la vue**. Sans cela, chaque appui sur « + » fait fuir le dessin
 * vers un coin et l'agent le rattrape à la main.
 */
export function zoomedBy(view: SchematicViewport, factor: number): SchematicViewport {
  const zoom = bounded((SCHEMATIC_VIEW.width / view.width) * factor)
  const width = SCHEMATIC_VIEW.width / zoom
  const height = SCHEMATIC_VIEW.height / zoom

  return {
    x: view.x + view.width / 2 - width / 2,
    y: view.y + view.height / 2 - height / 2,
    width,
    height,
  }
}

/**
 * Zoom **autour d'un point de l'écran** — le milieu des deux doigts qui pincent. Le point du
 * dessin qui était sous eux y reste : zoomer autour du centre ferait glisser le plan sous la main
 * à chaque pincement.
 */
export function zoomedAt(
  view: SchematicViewport,
  box: ViewportBox,
  client: { clientX: number; clientY: number },
  factor: number,
): SchematicViewport {
  const anchor = pointAt(view, box, client)
  const zoom = bounded((SCHEMATIC_VIEW.width / view.width) * factor)
  const width = SCHEMATIC_VIEW.width / zoom
  const height = SCHEMATIC_VIEW.height / zoom

  return {
    x: anchor.x - ((anchor.x - view.x) / view.width) * width,
    y: anchor.y - ((anchor.y - view.y) / view.height) * height,
    width,
    height,
  }
}

/**
 * La vue qui **ramène le dessin sous les yeux de l'agent**, à partir de la boîte qu'il occupe.
 *
 * ⚠️ Elle existe parce que le plan se perd : un glissement n'a pas de borne, et les deux boutons
 * de zoom opèrent **autour du centre de la vue courante** — ils ne rapprochent donc jamais d'un
 * dessin sorti du champ. Cinq glissements suffisaient à laisser l'agent, ganté et debout, devant
 * un plan blanc, avec pour seule issue de redessiner à cinq mille unités de son premier trait.
 *
 * ⚠️ **Elle ne grossit jamais au-delà de 100 %** : un dessin qui tient déjà dans le plan est
 * seulement recentré. Un bouton qui déplacerait *et* changerait l'échelle ferait deux gestes en
 * un, et l'agent perdrait le repère qu'il vient d'ajuster. Ce qui déborde, lui, est ramené —
 * jusqu'au plancher de zoom, qui garde la main : sous lui, le schéma disparaît dans un point.
 */
export function fittedTo(target: SchematicViewport): SchematicViewport {
  const zoom = bounded(
    Math.min(1, SCHEMATIC_VIEW.width / target.width, SCHEMATIC_VIEW.height / target.height),
  )
  const width = SCHEMATIC_VIEW.width / zoom
  const height = SCHEMATIC_VIEW.height / zoom

  return {
    x: target.x + target.width / 2 - width / 2,
    y: target.y + target.height / 2 - height / 2,
    width,
    height,
  }
}

/** Le grossissement affiché en bas à droite de la maquette — « 100 % » au repos. */
export function zoomPercent(view: SchematicViewport): number {
  return Math.round((SCHEMATIC_VIEW.width / view.width) * 100)
}

/**
 * L'outil « main » : le doigt tire le dessin, donc la vue se déplace **en sens inverse**.
 * L'écart arrive en pixels d'écran et se convertit, sinon le plan glisse d'autant plus vite
 * que l'agent est zoomé.
 */
export function pannedBy(
  view: SchematicViewport,
  box: ViewportBox,
  delta: { x: number; y: number },
): SchematicViewport {
  const scale = scaleOf(view, box)

  return {
    ...view,
    x: view.x - delta.x / scale,
    y: view.y - delta.y / scale,
  }
}

/**
 * Le nombre de pixels d'écran qu'occupe **une** unité de dessin — la même sur les deux axes,
 * comme le cadrage `xMidYMid meet` du navigateur.
 */
function scaleOf(view: SchematicViewport, box: ViewportBox): number {
  const scale = Math.min(box.width / view.width, box.height / view.height)

  return Number.isFinite(scale) && scale > 0 ? scale : 1
}

/** Les bandes vides que le centrage laisse sur le côté le plus long de la zone. */
function centeringOf(
  view: SchematicViewport,
  box: ViewportBox,
  scale: number,
): { x: number; y: number } {
  if (!(Number.isFinite(box.width) && box.width > 0 && box.height > 0)) return { x: 0, y: 0 }

  return {
    x: (box.width - view.width * scale) / 2,
    y: (box.height - view.height * scale) / 2,
  }
}

function bounded(zoom: number): number {
  return Math.min(MAX_SCHEMATIC_ZOOM, Math.max(MIN_SCHEMATIC_ZOOM, zoom))
}
